import { Course, Tutorial, CourseProgress, LearningPath, AvailableTutorial } from './types';
import { supabase } from '@/lib/supabase';

// Import all tutorial content using Vite's dynamic imports
const tutorialContentModules = {
  html: import.meta.glob('@/content/br/**/**/index.html', { as: 'raw', eager: true }),
  script: import.meta.glob('@/content/br/**/**/script.js', { as: 'raw', eager: true }),
  styles: import.meta.glob('@/content/br/**/**/styles.css', { as: 'raw', eager: true }),
  markdown: import.meta.glob('@/content/br/**/**/tutorial.md', { as: 'raw', eager: true }),
  test: import.meta.glob('@/content/br/**/**/test.js', { as: 'raw', eager: true }),
  quiz: import.meta.glob('@/content/br/**/**/quiz.json', { eager: true }),
};

/**
 * Helper to transform a tutorial record from the database into our Tutorial type.
 * It adds associated content and initial code by matching file paths.
 */
const transformTutorial = async (
  tutorialInfo: any,
  learningPathSlug: string,
  courseSlug: string
): Promise<Tutorial> => {
  // Use tutorial ID as is, without padding
  const tutorialId = String(tutorialInfo.id);
  console.debug('Tutorial ID:', { 
    id: tutorialId 
  });

  const findPath = (moduleGroup: Record<string, string>, fileName: string) => {
    const pathPattern = `/${learningPathSlug}/${courseSlug}/${tutorialId}/${fileName}`.toLowerCase();
    const matchingPath = Object.keys(moduleGroup).find((path) => {
      const normalizedPath = path.toLowerCase();
      return normalizedPath.includes(pathPattern);
    });
    
    if (!matchingPath) {
      console.warn(`Content file not found: ${pathPattern}`);
      console.debug('Available paths:', Object.keys(moduleGroup));
    }
    
    return matchingPath;
  };

  const markdownPath = findPath(tutorialContentModules.markdown, 'tutorial.md');
  const htmlPath = findPath(tutorialContentModules.html, 'index.html');
  const cssPath = findPath(tutorialContentModules.styles, 'styles.css');
  const jsPath = findPath(tutorialContentModules.script, 'script.js');
  const testPath = findPath(tutorialContentModules.test, 'test.js');
  const quizPath = findPath(tutorialContentModules.quiz, 'quiz.json');

  // Log content loading status
  console.debug('Tutorial content paths:', {
    tutorialId,
    markdownPath,
    htmlPath,
    cssPath,
    jsPath,
    testPath,
    quizPath
  });

  const tutorial = {
    id: tutorialInfo.id,
    slug: String(tutorialInfo.id), // Use ID as slug since they're equivalent now
    title: tutorialInfo.title,
    order: tutorialInfo.display_order,
    content: {
      markdown: markdownPath ? tutorialContentModules.markdown[markdownPath] : '',
      quiz: quizPath ? tutorialContentModules.quiz[quizPath] : null,
    },
    initial_code: {
      html: htmlPath ? tutorialContentModules.html[htmlPath] : '',
      css: cssPath ? tutorialContentModules.styles[cssPath] : '',
      js: jsPath ? tutorialContentModules.script[jsPath] : '',
      test: testPath ? tutorialContentModules.test[testPath] : '',
    },
    meta: tutorialInfo.meta || {},
    challenge: tutorialInfo.challenge,
    learningPathSlug,
    courseSlug,
    last_one: tutorialInfo.last_one || false,
    has_quiz: !!quizPath,
  };

  // Validate content
  if (!tutorial.content.markdown) {
    console.warn('Tutorial has no markdown content:', tutorial.slug);
  }

  return tutorial;
};

/**
 * Helper function to fetch a course given its slug and associated learning path slug.
 * Performs a join on learning_paths to verify the match.
 */
const getCourseBySlug = async (
  learningPathSlug: string,
  courseSlug: string
): Promise<Course | null> => {
  const { data, error } = await supabase
    .from('courses')
    .select('*, learning_paths(slug)')
    .eq('slug', courseSlug)
    .order('display_order', { ascending: true })
    .maybeSingle();

  if (error) {
    console.error('Error fetching course:', error);
    return null;
  }
  if (data && data.learning_paths && data.learning_paths.slug !== learningPathSlug) {
    return null;
  }
  return data;
};

export const learnApi = {
  async getLearningPaths(): Promise<LearningPath[]> {
    const { data, error } = await supabase
      .from('learning_paths')
      .select(`
        *,
        courses:courses(
          *,
          tutorials:tutorials(*)
        )
      `)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching learning paths:', error);
      return [];
    }
    return data || [];
  },

  async getCourses(): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
    return data || [];
  },

  async getCourse(
    learningPathSlug: string,
    courseSlug: string
  ): Promise<Course | null> {
    return getCourseBySlug(learningPathSlug, courseSlug);
  },

  async getTutorials(
    learningPathSlug: string,
    courseSlug: string
  ): Promise<Tutorial[]> {
    const course = await getCourseBySlug(learningPathSlug, courseSlug);
    if (!course) {
      console.error(`Course ${courseSlug} not found in learning path ${learningPathSlug}`);
      return [];
    }
    const { data, error } = await supabase
      .from('tutorials')
      .select('*')
      .eq('course_id', course.id)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching tutorials:', error);
      return [];
    }
    const tutorials = data || [];
    return Promise.all(
      tutorials.map((t) => transformTutorial(t, learningPathSlug, courseSlug))
    );
  },

  async getTutorial(
    learningPathSlug: string,
    courseSlug: string,
    tutorialId: number | string
  ): Promise<Tutorial | null> {
    const course = await getCourseBySlug(learningPathSlug, courseSlug);
    if (!course) {
      console.error(`Course ${courseSlug} not found in learning path ${learningPathSlug}`);
      return null;
    }

    // Handle both padded and unpadded IDs
    const unpadded = String(tutorialId).replace(/^0+/, ''); // Remove leading zeros
    
    const { data, error } = await supabase
      .from('tutorials')
      .select('*')
      .eq('id', unpadded)
      .eq('course_id', course.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching tutorial:', error);
      return null;
    }
    if (!data) {
      console.error(`Tutorial ${tutorialId} (unpadded: ${unpadded}) not found in course ${course.id}`);
      return null;
    }

    return transformTutorial(data, learningPathSlug, courseSlug);
  },

  async getUserProgress(userId: string): Promise<Record<number, any>> {
    const { data, error } = await supabase
      .from('user_tutorial_progress')
      .select('tutorial_id, completed_at')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching completed tutorials:', error);
      return {};
    }

    return (data || []).reduce((acc: Record<number, any>, progress: any) => {
      acc[progress.tutorial_id] = progress;
      return acc;
    }, {});
  },

  async getCourseProgress(
    learningPathSlug: string,
    courseSlug: string,
    userId: string
  ): Promise<CourseProgress> {
    const course = await getCourseBySlug(learningPathSlug, courseSlug);
    if (!course) {
      throw new Error(`Course ${courseSlug} not found in learning path ${learningPathSlug}`);
    }
    const tutorials = await this.getTutorials(learningPathSlug, courseSlug);
    const userProgress = await this.getUserProgress(userId);
    const tutorialIds = tutorials.map((t) => t.id);
    const isCompleted = tutorialIds.every((id) => userProgress[id] !== undefined);
    return {
      course_id: course.id,
      completed_tutorials: tutorialIds.filter((id) => userProgress[id] !== undefined),
      is_completed: isCompleted,
    };
  },

  async getAvailableTutorials(
    learningPathSlug: string,
    courseSlug: string,
    userId: string
  ): Promise<AvailableTutorial[]> {
    const tutorials = await this.getTutorials(learningPathSlug, courseSlug);
    const userProgress = await this.getUserProgress(userId);
    return tutorials.map((tutorial, index) => ({
      ...tutorial,
      isCompleted: Boolean(userProgress[tutorial.id]),
      isAvailable: index === 0 || Boolean(userProgress[tutorials[index - 1].id]),
    }));
  },

  async resetCourseProgress(
    learningPathSlug: string,
    courseSlug: string,
    userId: string
  ): Promise<boolean> {
    const course = await getCourseBySlug(learningPathSlug, courseSlug);
    if (!course) {
      throw new Error(`Course ${courseSlug} not found in learning path ${learningPathSlug}`);
    }
    const { error } = await supabase.rpc('reset_course_progress', {
      p_user_id: userId,
      p_course_id: course.id,
    });
    if (error) {
      console.error('Error resetting course progress:', error);
      return false;
    }
    return true;
  },

  async updateTutorialProgress(
    learningPathSlug: string,
    courseSlug: string,
    tutorialId: number | string,
    userId: string
  ): Promise<boolean> {
    try {
      console.log('Completing tutorial:', { tutorialId, userId });
      
      const { error } = await supabase.rpc('complete_tutorial', {
        p_user_id: userId,
        p_tutorial_id: Number(tutorialId)
      });

      if (error) {
        console.error('Error completing tutorial:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateTutorialProgress:', error);
      return false;
    }
  },

  async updateCourseProgress(
    learningPathSlug: string,
    courseSlug: string,
    userId: string
  ): Promise<boolean> {
    const course = await getCourseBySlug(learningPathSlug, courseSlug);
    if (!course) {
      console.error(`Course ${courseSlug} not found in learning path ${learningPathSlug}`);
      return false;
    }
    const { error } = await supabase.rpc('update_user_course_progress', {
      p_user_id: userId,
      p_course_id: course.id,
    });
    if (error) {
      console.error('[Course Progress] Error updating user course progress:', error);
      return false;
    }
    return true;
  },

  async updateLearningPathProgress(
    learningPathSlug: string,
    userId: string
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from('learning_paths')
      .select('*')
      .eq('slug', learningPathSlug)
      .maybeSingle();
    if (error || !data) {
      console.error(`Learning path ${learningPathSlug} not found`, error);
      return false;
    }
    const { error: rpcError } = await supabase.rpc('update_user_learning_path_progress', {
      p_user_id: userId,
      p_learning_path_id: data.id,
    });
    if (rpcError) {
      console.error('[Learning Path Progress] Error updating user learning path progress:', rpcError);
      return false;
    }
    return true;
  },

  async getUserCourseProgressDetails(userId: string): Promise<any[]> {
    // First get all courses with their tutorials
    const { data: courses, error: coursesError } = await supabase
      .from('courses')
      .select('*');

    if (coursesError) {
      console.error('Error fetching courses:', coursesError);
      return [];
    }

    // Get user's completed tutorials
    const { data: userProgress, error: progressError } = await supabase
      .from('user_course_progress')
      .select('*')
      .eq('user_id', userId);

    if (progressError) {
      console.error('Error fetching user course progress:', progressError);
      return [];
    }

    // Transform the data to match the previous format
    return courses.map(course => {
      const courseProgress = userProgress?.find(p => p.course_id === course.id);
      const total_tutorials = course.tutorials?.length || 0;
      const is_completed = !!courseProgress?.completed_at;

      return {
        course_id: course.id,
        course_title: course.course_title,
        course_slug: course.course_slug,
        learning_path_id: course.learning_path_id,
        is_completed,
        completed_tutorials: is_completed ? total_tutorials : 0,
        total_tutorials,
        completed_at: courseProgress?.completed_at
      };
    });
  },

  async getUserLearningPathProgressDetails(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('user_learning_path_completion_view')
      .select('*');
    if (error) {
      console.error('Error fetching user learning path progress details:', error);
      return [];
    }
    return data || [];
  },
};